import React, { useState } from 'react';
import { WidgetPanelDetailProps } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { PortfolioSummaryConfigurationState } from 'widgets/portfolioSummary/store/instances/configuration/PortfolioSummaryConfigurationState';
import { selectPortfolioSummaryAggregatedData } from 'widgets/portfolioSummary/store/data/selectors';
import {
    PortfolioSummaryAggregatedData,
    PortfolioSummaryAggregatedDataGroup,
    PortfolioSummaryAggregationItem,
} from 'widgets/portfolioSummary/store/data/PortfolioSummaryDataState';
import './WidgetPanelPortfolioSummary.scss';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';

export interface WidgetPanelPortfolioSummaryProps
    extends WidgetPanelDetailProps {
    showJson?: boolean;
}

export default function WidgetPanelPortfolioSummary(
    props: WidgetPanelPortfolioSummaryProps
) {
    const { state, widgetState, height, width, showJson } = props;

    const config = widgetState.config as PortfolioSummaryConfigurationState;

    const aggregatedData = selectPortfolioSummaryAggregatedData(
        state.state,
        config.aggregationField
    );

    return (
        <div className="widget-panel portfolio-summary-widget-panel">
            <div className="field">
                <span className={'label'}>Agg. Field:</span>
                {config.aggregationField}
            </div>

            {aggregatedData && !showJson && (
                <AggregatedData
                    aggregatedData={aggregatedData}
                    width={width - 20}
                    height={height - 115}
                />
            )}
            {aggregatedData && showJson && (
                <JsonPanel data={aggregatedData} key={'ps-ag'} />
            )}
        </div>
    );
}

export function AggregatedData(props: {
    aggregatedData: PortfolioSummaryAggregatedData;
    width: number;
    height: number;
}) {
    const { aggregatedData, width, height } = props;

    const [selectedGroup, setSelectedGroup] = useState('');

    const groupNames = Object.keys(aggregatedData);

    const style = { width, height };
    const groupNamesStyle = { height, width: 100 };

    const onClickGroup = (nextGroup: string) => {
        setSelectedGroup(nextGroup);
    };

    return (
        <div className="aggregated-data-panel" style={style}>
            <div className="group-names" style={groupNamesStyle}>
                {groupNames.map((g, i) => (
                    <div
                        key={'group-' + i}
                        className={`groupname${
                            g === selectedGroup ? ' selected' : ''
                        }`}
                        onClick={() => onClickGroup(g)}
                    >
                        {g}
                    </div>
                ))}
            </div>
            <AggregatedDataGroup
                groupName={selectedGroup}
                data={aggregatedData[selectedGroup]}
                width={width - 100}
                height={height}
            />
        </div>
    );
}

interface AggregatedDataGroupRow extends PortfolioSummaryAggregationItem {
    groupName: string;
}

function AggregatedDataGroup(props: {
    groupName: string;
    data: PortfolioSummaryAggregatedDataGroup;
    width: number;
    height: number;
}) {
    const { groupName, data, width, height } = props;
    const style = { width, height };

    if (!groupName) {
        return <div />;
    }

    const rows: AggregatedDataGroupRow[] = [];
    data?.total && rows.push({ groupName: 'total', ...data.total });
    data?.buy && rows.push({ groupName: 'buy', ...data.buy });
    data?.sell && rows.push({ groupName: 'sell', ...data.sell });
    data?.short && rows.push({ groupName: 'short', ...data.short });
    data?.net && rows.push({ groupName: 'net', ...data.net });

    const gridStyle = { height: height - 50 };
    return (
        <div className="values" style={style}>
            <div className="field">
                <span className={'label'}>Group Name</span>
                {groupName}
            </div>
            <WidgetPanelGrid
                data={rows}
                getRowDataId={(d) => d.groupName}
                csvFileName={'group-' + groupName}
                key={groupName + 'grid'}
                style={gridStyle}
            />
        </div>
    );
}

export function addPortfolioSummaryPanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const render = () => <WidgetPanelPortfolioSummary {...detailProps} />;
    panes.push({
        menuItem: 'Data',
        render: render,
    });

    const renderJson = () => (
        <WidgetPanelPortfolioSummary {...detailProps} showJson={true} />
    );
    panes.push({
        menuItem: 'Data Json',
        render: renderJson,
    });
}
